/**
 * Shared wording for a failed `POST /auth/sync`.
 *
 * Two components describe this same failure — the full-page AccountUnavailable
 * gate shown before the app renders, and the AccountBanner shown when sync
 * breaks mid-session. They must agree about what a given error means, so the
 * classification and the verbatim detail line live here rather than being
 * derived twice.
 */

import { config } from '../../config/appConfig.js';

export function describeSyncError(syncError) {
  const isAuthProblem = Boolean(syncError?.isAuthError);
  const isOffline = syncError?.code === 'NETWORK_ERROR';

  const message = isAuthProblem
    ? `We could not verify your sign-in with the ${config.appName} server. Signing in again usually fixes it.`
    : isOffline
      ? `You are signed in, but we could not reach ${config.appName} to load your library.`
      : `You are signed in, but your ${config.appName} profile did not load. Saving songs, playlists and history will not work until it does.`;

  /*
   * The underlying failure, shown verbatim.
   *
   * The friendly sentence above says what broke; without this line there is no
   * way to tell a 404 from a dev-proxy misroute from an expired token — which
   * is exactly the blindness this was added to end. Kept to one short line so
   * it informs without shouting.
   */
  const detail = [syncError?.status, syncError?.code, syncError?.message].filter(Boolean).join(' · ');

  return { isAuthProblem, isOffline, message, detail };
}
